// Helper utility functions

/**
 * Format file size to human readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Format date to readable string
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date
 */
export const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Format datetime to readable string
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted datetime
 */
export const formatDateTime2 = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatDateTime = (temps) => {
  const date = new Date(temps);

  const day = date.getDate();
  const month = date.toLocaleString('en-US', { month: 'short' });

  const time = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
  });

  return `${day} ${month} . ${time}`;
}
/**
 * Generate random ID
 * @param {number} length - Length of ID
 * @returns {string} Random ID
 */
export const generateId = (length = 8) => {
  return Math.random().toString(36).substring(2, length + 2);
};

/**
 * Generate unique ID with timestamp and random string
 * @returns {string} Unique ID in format: timestamp-random
 */
export const generateUniqueId = () => {
  const timestamp = Date.now().toString(36); // Convert current timestamp to base36 string
  const random = Math.random().toString(36).substr(2, 5); // Generate a random string
  return `${timestamp}-${random}`;
};

/**
 * Debounce function
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Truncate string
 * @param {string} str - String to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated string
 */
export const truncateString = (str, maxLength = 50) => {
  if (!str || str.length <= maxLength) return str;
  return str.substring(0, maxLength) + '...';
};

/**
 * Validate email
 * @param {string} email - Email to validate
 * @returns {boolean} Is valid email
 */
export const isValidEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

/**
 * Get file extension from filename
 * @param {string} filename - Filename
 * @returns {string} File extension
 */
export const getFileExtension = (filename) => {
  return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2);
};

/**
 * Check if file is image
 * @param {string} filename - Filename or mime type
 * @returns {boolean} Is image file
 */
export const isImageFile = (filename) => {
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
  const ext = getFileExtension(filename).toLowerCase();
  return imageExtensions.includes(ext) || filename.startsWith('image/');
};

/**
 * Check if file is video
 * @param {string} filename - Filename or mime type
 * @returns {boolean} Is video file
 */
export const isVideoFile = (filename) => {
  const videoExtensions = ['mp4', 'webm', 'ogg', 'mov', 'avi'];
  const ext = getFileExtension(filename).toLowerCase();
  return videoExtensions.includes(ext) || filename.startsWith('video/');
};

/**
 * Copy text to clipboard
 * @param {string} text - Text to copy
 */
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error('Failed to copy text:', err);
    return false;
  }
};

/**
 * Download file from URL
 * @param {string} url - File URL
 * @param {string} filename - Download filename
 */
export const downloadFile = (url, filename) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Get time ago string
 * @param {string|Date} date - Date
 * @returns {string} Time ago string
 */
export const timeAgo = (date) => {
  if (!date) return '';
  const seconds = Math.floor((new Date() - new Date(date)) / 1000);
  
  const intervals = {
    year: 31536000,
    month: 2592000,
    week: 604800,
    day: 86400,
    hour: 3600,
    minute: 60,
  };
  
  for (const [unit, secondsInUnit] of Object.entries(intervals)) {
    const interval = Math.floor(seconds / secondsInUnit);
    if (interval >= 1) {
      return `${interval} ${unit}${interval === 1 ? '' : 's'} ago`;
    }
  }
  
  return 'just now';
};

/**
 * Get device OS from user agent
 * @returns {string} Device OS (WEB, IOS, ANDROID)
 */
export const getDeviceOS = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  if (/android/i.test(userAgent)) {
    return 'ANDROID';
  }
  
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return 'IOS';
  }
  
  return 'WEB';
};

/**
 * Check if a timestamp is older than 24 hours
 * @param {string|Date} lastActive - Last active timestamp
 * @returns {boolean} True if older than 24 hours, false otherwise
 */
export const isOlderThan24Hours = (lastActive) => {
  if (!lastActive) return true; // If no lastActive, consider it inactive
  
  const lastActiveDate = new Date(lastActive);
  const currentTime = new Date();
  const timeDifference = currentTime - lastActiveDate;
  const hoursDifference = timeDifference / (1000 * 60 * 60);
  
  return hoursDifference > 24;
};

export default {
  formatFileSize,
  formatDate,
  formatDateTime,
  generateId,
  debounce,
  truncateString,
  isValidEmail,
  getFileExtension,
  isImageFile,
  isVideoFile,
  copyToClipboard,
  downloadFile,
  timeAgo,
  getDeviceOS,
  isOlderThan24Hours,
};

